import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Play } from 'lucide-react';
import Dialog from './ui/Dialog';
import { useStartTimer } from './home/useHomeData';
import { usePausedResumeStore } from '../store/pausedResumeStore';

/**
 * FIX-B17 (D-198 QX-147) — "להמשיך את [משימה]?" after the next timer stopped,
 * for a task that "אני על זה" paused and "עצירה ומעבר" left paused. The same
 * pattern as the break offer (BreakResumeOffer, PRESERVE #4, unchanged): an
 * offer, never an automatic start; "המשך" goes through the ONE start flow
 * (useStartTimer: clock-in, "עצירה ומעבר / שניהם", engagement).
 */
export default function PausedResumeOffer() {
  const { t } = useTranslation();
  const offered = usePausedResumeStore((s) => s.offered);
  const clear = usePausedResumeStore((s) => s.clear);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const startTimer = useStartTimer({ silentErrors: true });

  if (offered.length === 0) return null;

  const close = () => {
    clear();
    setError('');
  };

  const resume = async () => {
    setBusy(true);
    setError('');
    const failed: string[] = [];
    const together = offered.map((o) => o.ticket_id);
    for (const tm of offered) {
      try {
        await startTimer.mutateAsync({ id: tm.ticket_id, title: tm.ticket_title ?? '', runAlongside: together });
      } catch (err: any) {
        failed.push(`${tm.ticket_title ?? ''}: ${err?.error?.message || t('timer.startError')}`);
      }
    }
    setBusy(false);
    if (failed.length > 0) setError(failed.join(' · '));
    else close();
  };

  const titles = offered.map((o) => o.ticket_title || '').filter(Boolean).join(', ');

  return (
    <Dialog
      open
      onClose={close}
      title={t('timeTasks.pausedResume.title', { title: titles })}
      description={t('timeTasks.pausedResume.help')}
      icon={<Play size={18} className="text-success-700" aria-hidden="true" />}
      size="md"
      dismissible={!busy}
      footer={(
        <>
          <button type="button" onClick={close} className="btn btn-secondary" disabled={busy}>{t('timeTasks.pausedResume.notNow')}</button>
          <button type="button" onClick={resume} disabled={busy} aria-busy={busy || undefined} className="btn btn-primary">
            {t('timeTasks.pausedResume.resume')}
          </button>
        </>
      )}
    >
      {error && <p role="alert" className="rounded-lg border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-700">{error}</p>}
    </Dialog>
  );
}
